// Copyright (c) StrangeBee 2024

import client from '@Client';
import { ICONS } from '@Constants/iconsApp';
import { capitalizeFirstLetter } from '@Helpers/string';
import { parsePeriod } from '@Molecules/Widgets/utils';
import React, { ReactElement } from 'react';
import {
    ChartOutput,
    convertToSearchQuery,
    DefinitionOutput,
    DescribeOutput,
    ObjectTypeV1,
    SeriesInput,
    WidgetChartType,
    WidgetOptionsOutput,
} from 'thehive-sdk';

export const WidgetIcon: Record<WidgetChartType, ReactElement> = {
    bar: <ICONS.BarChartOutlined />,
    donut: <ICONS.PieChartOutlined />,
    line: <ICONS.LineChartOutlined />,
    text: <ICONS.ReferenceIcon />,
    counter: <ICONS.CalculatorOutlined />,
    radar: <ICONS.RadarChartOutlined />,
    gauge: <ICONS.GaugeChartOutlined />,
};

interface WidgetQueryOptions {
    options: WidgetOptionsOutput;
    definition?: DefinitionOutput;
    describeData?: DescribeOutput;
}

const line = ({
    definition,
    options: { interval = '1d', series = [] },
    describeData,
}: WidgetQueryOptions): Promise<ChartOutput[]> => {
    const { from, to } = getFromTo(definition);
    return client.ChartController.getChartTime({
        interval,
        from: from,
        to: to,
        cache: definition?.cache,
        aggs: series.map(
            (
                {
                    agg,
                    field,
                    periodDateField = '_createdAt',
                    filters = [],
                    entity = ObjectTypeV1.CASE,
                    dateField = '',
                },
                index,
            ) => ({
                name: `agg_${index}`,
                model: capitalizeFirstLetter(entity),
                dateField,
                filterDateField: periodDateField,
                filter: convertToSearchQuery(filters, true, describeData?.[entity]),
                agg: {
                    _agg: agg,
                    _name: `agg_${index}`,
                    _field: field,
                },
            }),
        ) as SeriesInput[],
    });
};

const donut = ({
    definition,
    options: { entity, field = '', filters = [], periodDateField = '_createdAt', size, withOther = true },
    describeData,
}: WidgetQueryOptions): Promise<ChartOutput[]> => {
    const { from, to } = getFromTo(definition);

    return client.ChartController.getChartCategory({
        model: capitalizeFirstLetter(entity),
        field,
        aggs: [
            {
                _agg: 'count',
                _name: 'count',
            },
        ],
        cache: definition?.cache,
        filter: convertToSearchQuery(
            [
                ...filters,
                ...(definition && definition?.period !== 'all'
                    ? [
                          {
                              field: periodDateField,
                              type: 'date',
                              operator: 'custom',
                              values: [from, to],
                          },
                      ]
                    : []),
            ],
            undefined,
            describeData?.[entity],
        ).query,
        size,
        withOther,
    });
};

const bar = ({
    definition,
    options: {
        interval = '1d',
        dateField = '_createdAt',
        periodDateField = '_createdAt',
        entity = ObjectTypeV1.CASE,
        filters = [],
        field,
    },
    describeData,
}: WidgetQueryOptions): Promise<ChartOutput[]> => {
    const { from, to } = getFromTo(definition);
    return client.ChartController.getChartTimeCategory({
        interval,
        from,
        to,
        cache: definition?.cache,
        aggs: [
            {
                name: 'field',
                model: capitalizeFirstLetter(entity),
                dateField,
                filterDateField: periodDateField,
                filter: convertToSearchQuery(filters, true, describeData?.[entity]),
                field,
                agg: {
                    _agg: 'count',
                    _name: 'count',
                },
            },
        ],
    });
};

const counter = ({
    definition,
    options: { filters = [], series = [], entity },
    describeData,
}: WidgetQueryOptions): Promise<ChartOutput> => {
    const { from, to } = getFromTo(definition);
    return client.ChartController.getChartQuery({
        series: series.map((serie, index) => {
            const { filters: serieFilters = [], periodDateField = '_createdAt', dateField = '' } = serie;

            const parsedFilters = [...filters, ...serieFilters];

            return {
                agg: {
                    _agg: serie.agg,
                    _name: `agg_${index}`,
                    _field: serie.field,
                },
                name: `agg_${index}`,
                filterDateField: periodDateField,
                dateField,
                filter: convertToSearchQuery(
                    definition && definition?.period !== 'all'
                        ? [
                              ...parsedFilters,
                              {
                                  field: periodDateField,
                                  type: 'date',
                                  operator: 'custom',
                                  values: [from, to],
                              },
                          ]
                        : parsedFilters,
                    true,
                    describeData?.[entity],
                ),
                model: capitalizeFirstLetter(entity),
            };
        }) as SeriesInput[],
        cache: definition?.cache,
    });
};

const text = ({
    definition,
    options: { filters = [], series = [] },
    describeData,
}: WidgetQueryOptions): Promise<ChartOutput> => {
    const { from, to } = getFromTo(definition);
    return client.ChartController.getChartQuery({
        series: series.map((serie, index) => {
            const {
                entity = ObjectTypeV1.CASE,
                filters: serieFilters = [],
                periodDateField = '_createdAt',
                dateField = '',
            } = serie;

            const parsedFilters = [...filters, ...serieFilters];

            return {
                agg: {
                    _agg: serie.agg,
                    _name: `agg_${index}`,
                    _field: serie.field,
                },
                name: `agg_${index}`,
                filterDateField: periodDateField,
                dateField,
                filter: convertToSearchQuery(
                    definition && definition?.period !== 'all'
                        ? [
                              ...parsedFilters,
                              {
                                  field: periodDateField,
                                  type: 'date',
                                  operator: 'custom',
                                  values: [from, to],
                              },
                          ]
                        : parsedFilters,
                    true,
                    describeData?.[entity],
                ),
                model: capitalizeFirstLetter(entity),
            };
        }) as SeriesInput[],
        cache: definition?.cache,
    });
};

const radar = ({
    definition,
    options: { filters = [], entity = ObjectTypeV1.CASE, field = '', periodDateField = '_createdAt' },
    describeData,
}: WidgetQueryOptions): Promise<ChartOutput[]> => {
    const { from, to } = getFromTo(definition);
    return client.ChartController.getChartCategory({
        model: capitalizeFirstLetter(entity),
        field,
        aggs: [
            {
                _agg: 'count',
                _name: 'count',
            },
        ],
        cache: definition?.cache,
        filter: convertToSearchQuery(
            [
                ...filters,
                ...(definition && definition?.period !== 'all'
                    ? [
                          {
                              field: periodDateField,
                              type: 'date',
                              operator: 'custom',
                              values: [from, to],
                          },
                      ]
                    : []),
            ],
            undefined,
            describeData?.[entity],
        ).query,
    });
};

const gauge = ({
    definition,
    options: { filters = [], entity = ObjectTypeV1.CASE, periodDateField = '_createdAt' },
    describeData,
}: WidgetQueryOptions): Promise<ChartOutput> => {
    const { from, to } = getFromTo(definition);
    return client.ChartController.getChartQuery({
        series: [
            {
                agg: {
                    _agg: 'count',
                },
                name: 'count',
                filterDateField: periodDateField,
                filter: convertToSearchQuery(
                    definition && definition?.period !== 'all'
                        ? [
                              ...filters,
                              {
                                  field: periodDateField,
                                  type: 'date',
                                  operator: 'custom',
                                  values: [from, to],
                              },
                          ]
                        : filters,
                    true,
                    describeData?.[entity],
                ),
                model: capitalizeFirstLetter(entity),
            },
        ] as SeriesInput[],
        cache: definition?.cache,
    });
};

function getFromTo(definition: DefinitionOutput | undefined) {
    if (definition === undefined) return {};
    const { period, customPeriod } = definition;
    return parsePeriod(customPeriod, period);
}

interface WidgetQueriesReturn {
    line: typeof line;
    donut: typeof donut;
    bar: typeof bar;
    counter: typeof counter;
    text: typeof text;
    radar: typeof radar;
    gauge: typeof gauge;
}

export const widgetQueries: WidgetQueriesReturn = {
    line,
    donut,
    bar,
    counter,
    text,
    radar,
    gauge,
};
